/**
 * Sidebar Navigation
 * 
 * Fixed left sidebar navigation with vertical menu items.
 * Great for content-heavy sites and dashboards.
 */

import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar, Users, Heart, ChevronRight, HelpCircle } from "lucide-react";
import { useNavigationItems } from "@/hooks/useNavigationItems";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { cn } from "@/lib/utils";
import { useHelpDialog } from "@/hooks/useHelpDialog";
import HelpDialog from "@/components/help/HelpDialog";

const NavigationSidebar = () => {
  const location = useLocation();
  const { features, navigation } = useSiteSettings();
  const config = useSiteConfig();
  const { navigationItems: navItems } = useNavigationItems();
  const helpDialog = useHelpDialog();

  const isActive = (path: string) => location.pathname === path;

  return (
    <aside className="fixed left-0 top-0 z-50 h-screen w-64 bg-primary text-primary-foreground border-r border-primary-foreground/10 flex flex-col">
      {/* Logo Section */}
      <div className="p-6 border-b border-primary-foreground/10">
        <Link to="/" className="flex items-center space-x-3">
          {config.logoUrl && config.logoUrl.trim() !== '' && (
            <img
              src={config.logoUrl}
              alt={config.organizationName}
              className="h-8 w-auto object-contain"
              onError={(e) => {
                console.error('[NavigationSidebar] Failed to load logo:', config.logoUrl);
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <span className="text-lg font-bold truncate">
            {config.organizationName}
          </span>
        </Link>
      </div>

      {/* Navigation Links */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={cn(
                "flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all",
                isActive(item.path)
                  ? "bg-heritage text-heritage-foreground"
                  : "text-primary-foreground/80 hover:bg-primary-foreground/10 hover:text-primary-foreground"
              )}
            >
              <span>{item.name}</span>
              {isActive(item.path) && <ChevronRight className="w-4 h-4" />}
            </Link>
          ))}
        </nav>

        {/* Divider */}
        <div className="my-6 border-t border-primary-foreground/10" />

        {/* CTA Section */}
        <div className="space-y-2 px-2">
          {features.events.enabled && navigation.showEventsButton && (
            <Button 
              variant="culture" 
              className="w-full justify-start" 
              asChild
            >
              <Link to="/events">
                <Calendar className="w-4 h-4 mr-2" />
                {features.events.buttonText}
              </Link>
            </Button>
          )}
          {features.membership.enabled && (
            <Button 
              variant="heritage" 
              className="w-full justify-start" 
              asChild
            >
              <Link to="/membership">
                <Users className="w-4 h-4 mr-2" />
                {features.membership.buttonText}
              </Link>
            </Button>
          )}
          {features.donation.enabled && (
            <Button 
              variant="donate" 
              className="w-full justify-start" 
              asChild
            >
              <Link to="/donate">
                <Heart className="w-4 h-4 mr-2" />
                {features.donation.buttonText}
              </Link>
            </Button>
          )}
          <Button
            variant="ghost"
            className="w-full justify-start text-primary-foreground"
            onClick={helpDialog.open}
            aria-label="Help"
          >
            <HelpCircle className="w-4 h-4" />
          </Button>
        </div>
      </ScrollArea>

      {/* Footer Section */}
      <div className="p-4 border-t border-primary-foreground/10">
        <p className="text-xs text-primary-foreground/60 text-center">
          © {new Date().getFullYear()} {config.organizationName}
        </p>
      </div>
      <HelpDialog open={helpDialog.isOpen} onOpenChange={helpDialog.setOpen} />
    </aside>
  );
};

export default NavigationSidebar;
